/**
 * WHICH OF MY CLUBS THE STOREFRONT MAY OFFER ME, AND WHEN IT MAY OFFER ANYTHING AT ALL.
 *
 * A player can belong to several clubs, and the shared pages (Tournaments, Ladders,
 * Players) deliberately keep showing all of them at once. So there is NO active club
 * anywhere in this product — no server field, no context, no stored id. This module
 * answers a much smaller question: standing on one club's storefront, which of my OTHER
 * clubs' storefronts is one tap away.
 *
 * THE SILENCE RULES ARE THE FEATURE. A storefront is a club's own paid surface, stripped
 * of our wordmark, our menu and its competitors. So the switcher appears only when it is
 * the member's own tool and never when it would be the platform naming other clubs on
 * this club's header:
 *
 *   - the viewer must belong to THIS club — otherwise the list would not even contain
 *     the club whose name it hangs off, and a stranger's storefront would end up
 *     listing three clubs he is not on;
 *   - at least one OTHER club must be reachable — a control that opens onto a list of
 *     one is noise, which is the common case (one club) and must render nothing;
 *   - a club with no public slug has no storefront to hop to, so it is not a row.
 *
 * `null` is therefore the ordinary answer, and it means "render nothing".
 *
 * SINCE 2026-09-07 THE SAME CONTROL HANGS OFF A SECOND BAR, and the rule did not fork to
 * serve it. `./storefrontReturn.ts` puts the club a visitor walked out of into the
 * PLATFORM header, with the same chevron beside it — and the club there may be one the
 * viewer has never joined, because that memory is about arrival and not membership. Only
 * the FIRST bullet above is affected, and only because it was never a fact about
 * switching: it is the storefront's own silence rule, an answer to "may a club's paid
 * page name other clubs", and a platform page is ours to answer for. So the caller says
 * which bar it is (`ClubSwitchHost`) and that one bullet is the only thing the answer
 * turns on. Everything else — the rows, the sort, the "one club is noise" floor, the
 * no-slug refusal — is read once, from here, for both.
 */

/**
 * Whose bar the switcher is hanging off.
 *
 *   'storefront'  the club's own page. Its silence rules bite: a viewer who does not
 *                 belong to THIS club is offered nothing at all.
 *   'platform'    one of ours (`/players`, `/tournaments`, …), carrying a note of where
 *                 this tab came from. The bar is ours, the club is a landmark on it, and
 *                 the list offered is the viewer's own clubs whether or not the
 *                 remembered club is among them.
 */
export type ClubSwitchHost = 'storefront' | 'platform'

/** Structurally what both `AuthOrganization` and `ProfileOrganizationRow` already are —
 *  so this module borrows the club list the session already loaded without importing
 *  either owner's type, and without anyone fetching it a second time. */
export type ClubMembership = {
  orgId?: string
  name?: string
  slug?: string | null
}

export type ClubSwitchRow = {
  key: string
  name: string
  slug: string
  /** The club's own storefront — the same public address the header's mark links to. */
  href: string
}

export type ClubSwitch = {
  /** The club the header names, when the viewer belongs to it — the row the storefront's
   *  panel marks "Here" and never links. `null` only on a platform bar, where the club
   *  in the header can be one the viewer has never joined; the panel there lists his own
   *  clubs and this row's whole job is to keep the named club out of that list. */
  current: ClubSwitchRow | null
  others: ClubSwitchRow[]
}

/** Where the picker sends someone whose club is not in the list. The one door out, and
 *  it lives inside a member-only popover rather than on the club's header. */
export const CLUB_SEARCH_HREF = '/clubs'

function slugOf(value: unknown): string {
  return String(value ?? '').trim().toLowerCase()
}

export function clubStorefrontHref(slug: string): string {
  return `/clubs/${encodeURIComponent(slug)}`
}

function rowFor(org: ClubMembership, slug: string, name?: string): ClubSwitchRow {
  return {
    key: slug,
    name: String(name || org.name || '').trim() || slug,
    slug,
    href: clubStorefrontHref(slug),
  }
}

/**
 * @param memberships the clubs this viewer belongs to (already in the session).
 * @param club the club the header names — its own slug and its own name, so the current
 *        row reads exactly as the club brands itself rather than as whatever the
 *        membership record happened to store.
 * @param host which bar is asking. Defaults to the storefront, so the club's own page
 *        keeps its silence rules by saying nothing.
 */
export function storefrontClubSwitch(
  memberships: ClubMembership[] | null | undefined,
  club: { slug?: string; name?: string } | null | undefined,
  host: ClubSwitchHost = 'storefront',
): ClubSwitch | null {
  const here = slugOf(club?.slug)
  if (!here) return null

  const rows = Array.isArray(memberships) ? memberships : []
  const seen = new Set<string>()
  let current: ClubSwitchRow | null = null
  const others: ClubSwitchRow[] = []

  for (const org of rows) {
    const slug = slugOf(org?.slug)
    if (!slug || seen.has(slug)) continue
    seen.add(slug)
    if (slug === here) current = rowFor(org, slug, club?.name)
    else others.push(rowFor(org, slug))
  }

  // Nowhere to hop is nothing to draw, on either bar.
  if (others.length === 0) return null
  // The storefront's own silence rule, and the only line `host` touches: a club's paid
  // page may not list clubs to a visitor who does not belong to it.
  if (host === 'storefront' && !current) return null
  // The session hands the clubs over in whatever order the server built them; a person
  // scanning a menu wants them alphabetical.
  others.sort((a, b) => a.name.localeCompare(b.name))
  return { current, others }
}
